import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/react";
import { ArrowLeft, ChefHat, Leaf, RefreshCw, Trash2, Utensils, HandHeart, Shapes } from "lucide-react";
import { apiFetch, coverUrl } from "@/lib/reels";
import { PrimaryButton } from "@/components/reels/controls";
import { BulkResults } from "@/components/reels/BulkResults";
import { Tile } from "@/components/reels/SavedBrowser";
import { useDownloadQueue } from "@/hooks/useDownloadQueue";
import { MovePill } from "@/components/reels/MovePill";

const GROUP_ICONS = { cuisine: Utensils, meal: ChefHat, dietary: Leaf, religious: HandHeart };
let categoriesCache = null;

export const useCategories = () => {
  const [groups, setGroups] = useState(categoriesCache);
  useEffect(() => {
    if (!categoriesCache) apiFetch("/categories").then((r) => r.json()).then((d) => { categoriesCache = d; setGroups(d); });
  }, []);
  return groups;
};

const ChipGroup = ({ icon: Icon, title, cats, counts, active, onPick, testId }) => (
  <div data-testid={testId}>
    <p className="mb-2.5 flex items-center gap-2 text-[17px] font-extrabold"><Icon size={20} className="text-[#d7352b]" />{title}</p>
    <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
      {cats.map((c) => (
        <button key={c.id} className="rs-chip" data-active={active === c.id} disabled={!counts[c.id]} onClick={() => onPick(c.id)} data-testid={`category-chip-${c.id}`}>
          {c.name}<span className="rs-chip-count">{counts[c.id] || 0}</span>
        </button>
      ))}
    </div>
  </div>
);

export const CategoryGroups = ({ cats, counts, active, onPick }) => (
  <div className="rs-material flex flex-col gap-5 p-4" data-testid="category-groups">
    {cats.groups.map((g) => (
      <ChipGroup key={g.id} icon={GROUP_ICONS[g.id]} title={g.name} cats={g.categories} counts={counts} active={active} onPick={onPick} testId={`category-group-${g.id}`} />
    ))}
    <ChipGroup icon={Shapes} title="More" cats={[cats.other, ...(counts.pending ? [{ id: "pending", name: "Not sorted yet" }] : [])]} counts={counts} active={active} onPick={onPick} testId="category-group-more" />
  </div>
);

export const CategoryVideos = ({ path, category, version, quality, cats, onMoved, showCreator = false }) => {
  const { getToken } = useAuth();
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const queue = useDownloadQueue(quality);

  useEffect(() => {
    (async () => {
      const res = await apiFetch(`${path}?category=${category}`, { token: await getToken() });
      setVideos(await res.json());
    })().catch(() => {});
  }, [path, category, version, getToken]);

  const moved = (id, to) => {
    setVideos((vs) => vs.filter((v) => v.id !== id || to === category));
    onMoved(id, to);
  };
  useEffect(() => setSelected(new Set()), [category]);

  const toggle = (url) => setSelected((s) => { const n = new Set(s); n.has(url) ? n.delete(url) : n.add(url); return n; });
  const all = videos.length > 0 && selected.size === videos.length;
  const picks = videos.filter((v) => selected.has(v.url));

  return (
    <>
      <div className="flex items-center justify-between">
        <span className="rs-secondary text-[13px]" data-testid="category-count">{videos.length} videos · {selected.size} selected</span>
        <button className="rs-bordered text-[13px]" disabled={!videos.length} data-testid="category-select-all-btn"
          onClick={() => setSelected(all ? new Set() : new Set(videos.map((v) => v.url)))}>{all ? "Deselect All" : "Select All"}</button>
      </div>
      <div className="grid max-h-[460px] grid-cols-3 gap-1.5 overflow-y-auto sm:grid-cols-4" data-testid="category-grid">
        {videos.map((v, i) => (
          <Tile key={v.id} m={{ ...v, thumbnail: coverUrl(v.id) }} i={i} testPrefix="category-tile" selected={selected.has(v.url)} onToggle={() => toggle(v.url)}
            footer={<>
              {showCreator && v.creator_username && <span className="pointer-events-none absolute left-1.5 top-1.5 max-w-[70%] truncate rounded-full bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold text-white" data-testid={`category-tile-${i}-creator`}>@{v.creator_username}</span>}
              <MovePill video={v} groups={cats} i={i} onMoved={moved} />
            </>} />
        ))}
      </div>
      <PrimaryButton testId="category-download-btn" busy={queue.busy} disabled={queue.busy || !picks.length}
        onClick={() => queue.run(picks.map((v) => ({ url: v.url, thumbnail: coverUrl(v.id), caption: v.caption })))}
        label={queue.busy ? "Downloading…" : picks.length ? `Download ${picks.length} Recipe${picks.length === 1 ? "" : "s"}` : "Select recipes to download"} />
      {queue.items.length > 0 && <BulkResults items={queue.items} busy={queue.busy} />}
    </>
  );
};

export const CreatorLibrary = ({ creatorId, onBack, quality }) => {
  const { getToken } = useAuth();
  const cats = useCategories();
  const [c, setC] = useState(null);
  const [active, setActive] = useState(null);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    const res = await apiFetch(`/creators/${creatorId}`, { token: await getToken() });
    setC(await res.json());
    setVersion((v) => v + 1);
  }, [creatorId, getToken]);

  const busy = c && (c.status === "importing" || c.status === "categorizing");
  useEffect(() => { load().catch((e) => setError(e.message)); }, [load]);
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [busy, load]);

  const act = async (path, method) => {
    setError("");
    try {
      await apiFetch(path, { method, token: await getToken() });
      if (method === "DELETE") return onBack();
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!c || !cats) return <div className="flex justify-center py-6"><span className="rs-spinner" /></div>;
  const counts = c.counts || {};
  const pct = c.total ? Math.round((c.categorized / c.total) * 100) : 0;

  return (
    <div className="flex flex-col gap-4 rs-fade" data-testid="creator-library">
      <div className="rs-material flex flex-col gap-3 p-4">
        <div className="flex items-center gap-2">
          <button className="rs-bordered !px-2.5" onClick={onBack} aria-label="Back" data-testid="creator-back-btn"><ArrowLeft size={16} /></button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[18px] font-extrabold" data-testid="creator-username">@{c.username}</p>
            <p className="rs-secondary text-[12px]" data-testid="creator-status">
              {c.platform === "tiktok" ? "TikTok" : "Instagram"} · {c.total} videos · {c.categorized} sorted
            </p>
          </div>
          <button className="rs-bordered !px-2.5" disabled={busy} onClick={() => act(`/creators/${creatorId}/refresh`, "POST")} aria-label="Refresh" data-testid="creator-refresh-btn"><RefreshCw size={16} /></button>
          <button className="rs-bordered !px-2.5 text-[#c62d22]" disabled={busy} onClick={() => window.confirm(`Remove @${c.username}'s library?`) && act(`/creators/${creatorId}`, "DELETE")} aria-label="Delete" data-testid="creator-delete-btn"><Trash2 size={16} /></button>
        </div>
        {busy && (
          <div data-testid="creator-import-progress">
            <div className="rs-progress"><div className="rs-progress-fill" style={{ transform: `scaleX(${Math.max(0.03, pct / 100)})` }} /></div>
            <p className="rs-secondary mt-1 text-[12px]">{c.status === "importing" ? "Pulling videos from the profile and sorting as they arrive" : "Sorting the last videos"} · {pct}%</p>
          </div>
        )}
        {(error || c.error) && <p className="text-[13px] text-[#c62d22]" data-testid="creator-error">{error || c.error}</p>}
      </div>

      <CategoryGroups cats={cats} counts={counts} active={active} onPick={setActive} />

      {active && (
        <div className="rs-material flex flex-col gap-3 p-4" data-testid="category-panel">
          <CategoryVideos path={`/creators/${creatorId}/videos`} category={active} version={version} quality={quality} cats={cats} onMoved={() => load()} />
        </div>
      )}
    </div>
  );
};
